/**
 * draftComposition.js
 * ---------------------------------------------------------------------------
 * SINGLE source of truth for reading a team's draft composition (per-position
 * current counts and roster limits) out of a backend draft payload.
 *
 * WHY THIS EXISTS
 * Three consumers (PackRecommendationEngine, DraftProgressTracker,
 * SequentialPackSystem) each re-derived composition with slightly different
 * key guesses. That divergence is exactly how the `composition.<pos>.target`
 * bug shipped: the serializer emits `max`, nothing emits `target`, so every
 * lookup missed and silently fell through to hardcoded daily-league numbers
 * (QB 1 / RB 2 / WR 2 / GEAR 5). Non-default catalogs (e.g. a 9-player season
 * catalog at 2 QB / 4 RB / 3 WR) then got wrong scores, wrong pack filtering
 * and wrong needs summaries — with no error anywhere.
 *
 * VERIFIED BACKEND SHAPES (read from source, not assumed)
 *
 * 1. CANONICAL — TeamDraftStateSerializer.get_team_composition(),
 *    backend/game/api/serializers/pack_serializers.py:232-239:
 *        {
 *          'qb':   {'current': int, 'max': int},
 *          'rb':   {'current': int, 'max': int},
 *          'wr':   {'current': int, 'max': int},
 *          'gear': {'current': int, 'max': int},
 *        }
 *    Sub-keys are exactly `current` and `max`. There is NO `target` key.
 *    This is what lands on `draft_state.team_composition` for every draft
 *    endpoint (draft-status, next-pack, select, start, autodraft).
 *
 * 2. LEGACY FLAT — several non-`draft_state` envelopes still emit flat counts:
 *      - SelectFromPackView._get_draft_progress(), pack_views.py:700-715 →
 *        `draft_progress.current_composition` = {qb_count, rb_count, wr_count,
 *        gear_count}, with limits in a SIBLING `position_limits` map
 *        {max_qb, max_rb, max_wr, max_gear}.
 *      - Team.get_draft_progress(), backend/football/models.py:902-907 →
 *        `draft_progress.team_composition` = {qb_count, ...} with NO limits.
 *      - UnifiedPackGenerationService, pack_unified.py:271-276 → same flat
 *        counts, no limits.
 *    Note the flat shape can appear under the key name `team_composition` too,
 *    so shape detection is done per-object, never by key name.
 *
 * NO SILENT FAILURES
 * A missing count or limit resolves to `null`, never to a plausible hardcoded
 * roster size, and every miss is reported once per call via console.error with
 * the position(s) requested, the actual composition object and its keys.
 * Callers must handle `null` by degrading visibly (render '—', skip the
 * heuristic) — never by substituting a number.
 */

export const COMPOSITION_POSITIONS = ['qb', 'rb', 'wr', 'gear'];

/** Flat-shape limit key for a position, as emitted in `position_limits`. */
const FLAT_LIMIT_KEYS = {
    qb: 'max_qb',
    rb: 'max_rb',
    wr: 'max_wr',
    gear: 'max_gear'
};

function isFiniteNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
}

function emptyComposition() {
    const empty = {};
    COMPOSITION_POSITIONS.forEach(position => {
        empty[position] = { current: null, max: null };
    });
    empty._shape = 'missing';
    return empty;
}

/**
 * Detect which verified shape a raw composition object is in.
 * @returns {'nested'|'flat'|'unknown'}
 */
function detectShape(raw) {
    if (!raw || typeof raw !== 'object') return 'unknown';

    const hasNested = COMPOSITION_POSITIONS.some(
        position => raw[position] && typeof raw[position] === 'object'
    );
    if (hasNested) return 'nested';

    const hasFlat = COMPOSITION_POSITIONS.some(
        position => raw[`${position}_count`] !== undefined
    );
    if (hasFlat) return 'flat';

    return 'unknown';
}

/**
 * Normalize any verified backend composition payload into
 * `{ qb: {current, max}, rb: {...}, wr: {...}, gear: {...}, _shape }`.
 *
 * @param {object} teamState - a `draft_state` (canonical) or `draft_progress`
 *   (legacy flat) payload. Reads `team_composition` / `current_composition`
 *   and, for the flat shape, the sibling `position_limits` map.
 * @param {string} context - caller label, included in error output.
 * @returns {object} normalized composition; values are `number` or `null`.
 */
export function normalizeComposition(teamState, context = 'unknown') {
    if (!teamState || typeof teamState !== 'object') {
        console.error(
            `[draftComposition:${context}] CONTRACT VIOLATION: no team state object to read ` +
            'team_composition from. Expected the backend draft_state payload.',
            { teamState, teamState_typeof: typeof teamState }
        );
        return emptyComposition();
    }

    const raw = teamState.team_composition ?? teamState.current_composition;
    const shape = detectShape(raw);

    if (shape === 'unknown') {
        console.error(
            `[draftComposition:${context}] CONTRACT VIOLATION: team_composition is missing or in ` +
            'an unrecognized shape. Expected the canonical nested shape from ' +
            'TeamDraftStateSerializer.get_team_composition() ' +
            '(pack_serializers.py:232) — {qb: {current, max}, rb: {...}, wr: {...}, gear: {...}}.',
            {
                composition: raw,
                compositionKeys: (raw && typeof raw === 'object') ? Object.keys(raw) : null,
                teamStateKeys: Object.keys(teamState)
            }
        );
        return emptyComposition();
    }

    if (shape === 'flat') {
        // Genuine legacy producers still emit this (pack_views.py:703,
        // models.py:902, pack_unified.py:271). Log every time it is taken so
        // the path can be retired once those envelopes are migrated.
        console.warn(
            `[draftComposition:${context}] LEGACY SHAPE: reading flat <pos>_count composition ` +
            'keys. Producers: pack_views.py:703 (draft_progress.current_composition), ' +
            'models.py:902 (Team.get_draft_progress), pack_unified.py:271. ' +
            'The canonical nested {current, max} shape should be preferred — retire this path ' +
            'once those envelopes emit it.',
            { composition: raw, compositionKeys: Object.keys(raw) }
        );
    }

    const limits = teamState.position_limits;
    const normalized = { _shape: shape };
    const missing = [];

    COMPOSITION_POSITIONS.forEach(position => {
        let current = null;
        let max = null;

        if (shape === 'nested') {
            const entry = raw[position];
            if (entry && typeof entry === 'object') {
                if (isFiniteNumber(entry.current)) current = entry.current;
                else missing.push(`${position}.current`);

                if (isFiniteNumber(entry.max)) max = entry.max;
                else missing.push(`${position}.max`);
            } else {
                missing.push(`${position} (entry absent)`);
            }
        } else {
            const flatCount = raw[`${position}_count`];
            if (isFiniteNumber(flatCount)) current = flatCount;
            else missing.push(`${position}_count`);

            const limitValue = limits ? limits[FLAT_LIMIT_KEYS[position]] : undefined;
            if (isFiniteNumber(limitValue)) max = limitValue;
            else missing.push(`position_limits.${FLAT_LIMIT_KEYS[position]}`);
        }

        normalized[position] = { current, max };
    });

    if (missing.length > 0) {
        console.error(
            `[draftComposition:${context}] CONTRACT VIOLATION: composition is missing expected ` +
            `field(s): ${missing.join(', ')}. Roster limits are catalog-driven and vary per ` +
            'league template (e.g. season 2 QB / 4 RB / 3 WR vs daily 1/2/2) — a hardcoded ' +
            'default would be WRONG, so these resolve to null and callers degrade visibly.',
            {
                detectedShape: shape,
                composition: raw,
                compositionKeys: Object.keys(raw),
                positionLimits: limits ?? null,
                teamStateKeys: Object.keys(teamState)
            }
        );
    }

    return normalized;
}

/**
 * Current drafted count for a position.
 * @returns {number|null} null when the payload did not supply it (already logged).
 */
export function getPositionCount(composition, position) {
    return composition?.[position]?.current ?? null;
}

/**
 * Roster limit (the serializer's `max`) for a position.
 * @returns {number|null} null when the payload did not supply it (already logged).
 */
export function getPositionLimit(composition, position) {
    return composition?.[position]?.max ?? null;
}

/**
 * How many more of a position the team may still draft.
 * @returns {number|null} null when count or limit is unknown — callers MUST NOT
 *   substitute a guess.
 */
export function getPositionNeed(composition, position) {
    const current = getPositionCount(composition, position);
    const max = getPositionLimit(composition, position);
    if (!isFiniteNumber(current) || !isFiniteNumber(max)) return null;
    return Math.max(0, max - current);
}
